import { useState, useEffect, useCallback, useRef } from 'react';
import { cenowkiService } from '../../services/cenowkiService';
import cenowkiBufferService from '../../services/cenowkiBufferService';
import cenowkiSettingsService from '../../services/cenowkiSettingsService';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  extractPackageQuantity,
  extractWeight,
  extractManufacturer,
  extractUnitAndQuantity,
  getDisplayPrice
} from './pricingUtils';

const useLabelBuffer = ({ selectedLocation, user, manufacturers, measureUnits, routerLocation }) => {
  const saveTimeoutRef = useRef(null);

  const [labelBuffer, setLabelBuffer] = useState([]);
  const [showBuffer, setShowBuffer] = useState(false);
  const [bufferLoading, setBufferLoading] = useState(false);

  // Label settings
  const defaultLabelSettings = {
    includeManufacturer: true,
    includeSimplifiedName: true,
    includeQuantity: true,
    includePrice: true,
    includeSpecialPrice: true,
    includeWeight: true,
    fontSize: 'medium',
    layout: 'compact',
    priceFontSize: 22,
    productNameFontSize: 10,
    manufacturerFontSize: 8,
    unitsFontSize: 10,
    lineSpacing: 1
  };

  const [labelSettings, setLabelSettings] = useState(defaultLabelSettings);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewProducts, setPreviewProducts] = useState([]);
  const [copyMultiplier, setCopyMultiplier] = useState(1);
  const [selectedForCopy, setSelectedForCopy] = useState(new Set());

  // Load settings from DB
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const currentUser = user?.login || 'default';
        const response = await cenowkiSettingsService.getSettings(currentUser);
        if (response.success && response.data) {
          setLabelSettings({ ...defaultLabelSettings, ...response.data });
        }
      } catch (e) {
        console.error('Błąd ładowania ustawień cenówek:', e);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Save settings with debounce
  useEffect(() => {
    if (!settingsLoaded) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSettingsSaved(false);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const currentUser = user?.login || 'default';
        const result = await cenowkiSettingsService.saveSettings(currentUser, labelSettings);
        if (result.success) {
          setSettingsSaved(true);
          setTimeout(() => setSettingsSaved(false), 2000);
        }
      } catch (e) {
        console.error('Błąd zapisywania ustawień cenówek:', e);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [labelSettings, settingsLoaded, user]);

  // Load buffer from server
  const loadBuffer = useCallback(async () => {
    if (!selectedLocation?.id) return;
    setBufferLoading(true);
    try {
      const result = await cenowkiBufferService.getBuffer(selectedLocation.id);
      if (result.success && result.data) {
        const items = cenowkiBufferService.parseBufferItems(result.data);
        setLabelBuffer(items);
        localStorage.setItem('cenowki_label_buffer', JSON.stringify(items));
      }
    } catch (err) {
      console.error('Błąd ładowania bufora:', err);
      try {
        const cached = JSON.parse(localStorage.getItem('cenowki_label_buffer') || '[]');
        setLabelBuffer(cached);
      } catch {}
    } finally {
      setBufferLoading(false);
    }
  }, [selectedLocation?.id]);

  // Load buffer on location change
  useEffect(() => {
    loadBuffer();
  }, [loadBuffer]);

  // Check URL param showBuffer
  useEffect(() => {
    const params = new URLSearchParams(routerLocation?.search || '');
    if (params.get('showBuffer') === 'true') {
      loadBuffer();
      setShowBuffer(true);
    }
  }, [routerLocation?.search, loadBuffer]);

  // Add to buffer
  const addToBuffer = async (product, cenowkaData = null) => {
    if (!selectedLocation) {
      toast.error('Proszę najpierw wybrać magazyn');
      return;
    }

    let finalCenowkaData = cenowkaData;

    if (!cenowkaData) {
      try {
        const locationIdVal = selectedLocation?.location_id || selectedLocation;
        const cenowkaResponse = await cenowkiService.getCenowkaByProduct(product.id, locationIdVal);
        const existingCenowka = cenowkaResponse?.data;

        if (existingCenowka) {
          finalCenowkaData = {
            nazwa_uproszczona: existingCenowka.nazwa_uproszczona || '',
            cena_cenowkowa: existingCenowka.cena_cenowkowa || '',
            cena_promocyjna: existingCenowka.cena_promocyjna || '',
            typ_cenowki: existingCenowka.typ_cenowki || 'standardowa',
            kategoria_cenowki: existingCenowka.kategoria_cenowki || extractManufacturer(product.nazwa, product.opis, manufacturers),
            opis_cenowki: existingCenowka.opis_cenowki || '',
            waga: existingCenowka.waga || product.gramatura || product.ilosc_jednostek || 0,
            jednostka_wagi: existingCenowka.jednostka_wagi || product.jednostka_wagi || 'nieustawiono'
          };
        }
      } catch (error) {
        // label fetch for buffer failed
      }
    }

    const extracted = extractUnitAndQuantity(product.nazwa, product.opis, measureUnits);
    const detectedManufacturer = extractManufacturer(product.nazwa, product.opis, manufacturers);
    const priceInfo = getDisplayPrice(product);
    const productWithCenowka = {
      ...product,
      // Producent jawnie: z listy produktow (producent/producent_id) lub wykryty z nazwy -
      // trafia do product_data w buforze, zeby podglad bufora mial go od razu
      manufacturer: product.manufacturer || product.producent || detectedManufacturer || null,
      nazwa_uproszczona: finalCenowkaData?.nazwa_uproszczona || product.nazwa_uproszczona || product.nazwa,
      finalName: finalCenowkaData?.nazwa_uproszczona || product.nazwa_uproszczona || product.nazwa,
      cenowka: finalCenowkaData || {
        cena_cenowkowa: priceInfo.price,
        waga: product.gramatura || product.ilosc_jednostek || extracted.quantity || 0,
        jednostka_wagi: product.jednostka_wagi || extracted.unit || 'nieustawiono',
        kategoria_cenowki: detectedManufacturer
      },
      bufferId: Date.now() + Math.random()
    };

    if (labelBuffer.find(item => item.id === product.id)) {
      toast.error('Ten produkt już jest w buforze cenówek');
      return;
    }

    const result = await cenowkiBufferService.addToBuffer(selectedLocation.id, productWithCenowka, 'cenowki');

    if (result.success) {
      await loadBuffer();
    } else {
      toast.error(result.error || 'Błąd dodawania do bufora');
    }
  };

  const removeFromBuffer = async (productId) => {
    if (!selectedLocation?.id) return;
    const result = await cenowkiBufferService.removeFromBuffer(selectedLocation.id, productId);
    if (result.success) {
      await loadBuffer();
    }
  };

  const clearBuffer = async () => {
    if (!selectedLocation?.id) return;
    if (window.confirm('Czy na pewno wyczyścić bufor cenówek?')) {
      const result = await cenowkiBufferService.clearBuffer(selectedLocation.id);
      if (result.success) {
        setLabelBuffer([]);
        localStorage.setItem('cenowki_label_buffer', '[]');
      }
    }
  };

  // Enrich buffer products with full data
  const enrichBufferProducts = async (bufferProducts) => {
    const locationIdVal = selectedLocation?.location_id || selectedLocation;
    const priceLocationId = selectedLocation?.id || locationIdVal;

    const enrichedProducts = await Promise.all(bufferProducts.map(async (bufferProduct) => {
      try {
        const response = await api.get(`/products/${bufferProduct.id}`, {
          params: { location_id: priceLocationId }
        });
        const fullProduct = response.data?.data || response.data || {};

        // Swieza cena efektywna per lokalizacja (specjalna magazynu -> karta);
        // snapshot z bufora tylko jako fallback offline
        const freshPrices = cenowkiBufferService.getEffectivePrices(fullProduct);

        let finalName = bufferProduct.finalName || bufferProduct.nazwa_uproszczona || bufferProduct.nazwa;
        try {
          const cenowkaResponse = await cenowkiService.getCenowkaByProduct(bufferProduct.id, locationIdVal);
          const existingCenowka = cenowkaResponse?.data;
          if (existingCenowka && existingCenowka.nazwa_uproszczona) {
            finalName = existingCenowka.nazwa_uproszczona;
          }
        } catch (e) {
          // label fetch failed
        }

        return {
          ...fullProduct,
          ...bufferProduct,
          id: bufferProduct.id,
          simplifiedName: finalName,
          finalName: finalName,
          packageQuantity: extractPackageQuantity(fullProduct, fullProduct.nazwa || bufferProduct.nazwa, fullProduct.opis, measureUnits),
          weight: extractWeight(fullProduct.nazwa || bufferProduct.nazwa, fullProduct.opis),
          manufacturer: fullProduct.producent || extractManufacturer(fullProduct.nazwa || bufferProduct.nazwa, null, manufacturers),
          displayPrice: freshPrices?.brutto ?? bufferProduct.cena_sprzedazy_brutto ?? fullProduct.cena_sprzedazy_brutto,
          cena_sprzedazy_brutto: freshPrices?.brutto ?? bufferProduct.cena_sprzedazy_brutto ?? fullProduct.cena_sprzedazy_brutto,
          cena_sprzedazy_netto: freshPrices?.netto ?? bufferProduct.cena_sprzedazy_netto ?? fullProduct.cena_sprzedazy_netto
        };
      } catch (error) {
        // product enrichment failed
        return {
          ...bufferProduct,
          simplifiedName: bufferProduct.finalName || bufferProduct.nazwa,
          finalName: bufferProduct.finalName || bufferProduct.nazwa,
          displayPrice: bufferProduct.cena_sprzedazy_brutto
        };
      }
    }));

    return enrichedProducts;
  };

  const handleTransferBufferToPreview = async () => {
    if (labelBuffer.length === 0) return;

    const enrichedProducts = await enrichBufferProducts(labelBuffer);

    setPreviewProducts(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const newProducts = enrichedProducts.filter(p => !existingIds.has(p.id));
      return [...prev, ...newProducts];
    });

    setShowBuffer(false);
    setShowPreview(true);
  };

  const handleLoadBufferToPreview = async () => {
    if (labelBuffer.length === 0) return;

    const enrichedProducts = await enrichBufferProducts(labelBuffer);

    setPreviewProducts(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const newProducts = enrichedProducts.filter(p => !existingIds.has(p.id));
      return [...prev, ...newProducts];
    });
  };

  // Copy selection for duplicating labels
  const handleToggleCopySelection = (productId) => {
    const newSelected = new Set(selectedForCopy);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedForCopy(newSelected);
  };

  const handleSelectAllForCopy = () => {
    setSelectedForCopy(new Set(previewProducts.map(p => p.id)));
  };

  const handleDeselectAllForCopy = () => {
    setSelectedForCopy(new Set());
  };

  // Save settings explicitly
  const handleSaveSettings = async () => {
    try {
      const currentUser = user?.login || 'default';
      const result = await cenowkiSettingsService.saveSettings(currentUser, labelSettings);
      if (result.success) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
      }
    } catch (e) {
      console.error('Błąd zapisywania:', e);
    }
  };

  return {
    // Buffer
    labelBuffer,
    showBuffer,
    setShowBuffer,
    bufferLoading,
    loadBuffer,
    addToBuffer,
    removeFromBuffer,
    clearBuffer,

    // Label settings
    labelSettings,
    setLabelSettings,
    settingsSaved,
    handleSaveSettings,

    // Preview
    showPreview,
    setShowPreview,
    previewProducts,
    setPreviewProducts,

    // Copy duplication
    copyMultiplier,
    setCopyMultiplier,
    selectedForCopy,
    setSelectedForCopy,
    handleToggleCopySelection,
    handleSelectAllForCopy,
    handleDeselectAllForCopy,

    // Transfer
    handleTransferBufferToPreview,
    handleLoadBufferToPreview,
    enrichBufferProducts,
  };
};

export default useLabelBuffer;
